import { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { requestTypeSettingsApi } from '../../lib/api';

const ROLE_OPTIONS = {
  CTA: [
    { key: 'COORDINATOR', label: 'Assigned Coordinators' },
    { key: 'CHAIRMAN', label: 'Chairman' },
    { key: 'ADMIN', label: 'Administrator' },
    { key: 'CRD', label: 'Admin Support (CRD)' },
    { key: 'DIRECTOR', label: 'Director' },
    { key: 'EXEC_DIRECTOR', label: 'Executive Director' },
    { key: 'FINANCE', label: 'Finance' },
  ],
  FT: [
    { key: 'FINANCIAL_OFFICER', label: 'Assigned Financial Officer' },
    { key: 'COORDINATOR', label: 'Assigned Coordinators' },
    { key: 'CHAIRMAN', label: 'Chairman' },
    { key: 'ADMIN', label: 'Administrator' },
    { key: 'CRD', label: 'Admin Support (CRD)' },
    { key: 'DIRECTOR', label: 'Director' },
    { key: 'EXEC_DIRECTOR', label: 'Executive Director' },
    { key: 'FINANCE', label: 'Finance' },
  ],
};

export function BundleToolConfig({ requestType }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState([]);
  const [visibility, setVisibility] = useState('INTERNAL');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await requestTypeSettingsApi.getBundleToolConfig(requestType);
      setRoles(res.data.enabled_roles || []);
      setVisibility(res.data.default_visibility || 'INTERNAL');
    } catch {
      toast.error('Failed to load bundle tool configuration');
    } finally {
      setLoading(false);
    }
  }, [requestType]);

  useEffect(() => { load(); }, [load]);

  const toggleRole = (key) =>
    setRoles((prev) => (prev.includes(key) ? prev.filter((r) => r !== key) : [...prev, key]));

  const handleSave = async () => {
    setSaving(true);
    try {
      await requestTypeSettingsApi.updateBundleToolConfig(requestType, {
        enabled_roles: roles,
        default_visibility: visibility,
      });
      toast.success('Bundle tool configuration saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-slate-500 py-4"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  }

  return (
    <div className="space-y-4" data-testid={`bundle-tool-config-${requestType}`}>
      <p className="text-sm text-slate-500 max-w-xl">
        The "Generate Bundle PDF" button in the Documents tab combines any uploaded documents into
        one PDF (with optional request-form cover pages). Choose who sees the tool on {requestType} requests
        and the default visibility of generated bundles.
      </p>
      <div>
        <Label className="text-sm font-semibold text-slate-700">Who can use the tool</Label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
          {(ROLE_OPTIONS[requestType] || []).map((opt) => (
            <label
              key={opt.key}
              className="flex items-center gap-2 p-2 rounded border border-slate-200 bg-white cursor-pointer hover:bg-slate-50"
              data-testid={`bundle-tool-role-${opt.key}`}
            >
              <Checkbox checked={roles.includes(opt.key)} onCheckedChange={() => toggleRole(opt.key)} />
              <span className="text-sm">{opt.label}</span>
            </label>
          ))}
        </div>
      </div>
      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <Label className="text-sm font-semibold text-slate-700">Default bundle visibility</Label>
          <Select value={visibility} onValueChange={setVisibility}>
            <SelectTrigger className="w-[260px] mt-1" data-testid="bundle-tool-visibility-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="INTERNAL">Internal only (hidden from Company Reps)</SelectItem>
              <SelectItem value="COMPANY">Visible to Company Reps too</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-blue-900 hover:bg-blue-950" data-testid="bundle-tool-save-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save
        </Button>
      </div>
    </div>
  );
}

export default BundleToolConfig;
